<template>
  <div class="input__wrapper" :class="inputWrapperClass">
    <transition name="fade-spinner" mode="out-in">
      <div v-if="!isPrefilled" class="input__component-wrapper">
        <input
          v-if="!multiline"
          ref="input"
          :value="value"
          :type="inputType"
          :aria-label="ariaLabel || placeholder"
          :maxlength="maxLength"
          :class="inputClass"
          v-bind="inputAttrs"
          @click="clickHandler"
          @focus="focusHandler"
          @blur="blurHandler"
          @keypress="keypressHandler"
          @keyup="$emit('keyup', $event)"
          @input="$emit('input', $event.target.value)"
          @keydown="$emit('keydown', $event)"
        />
        <textarea
          v-else
          ref="input"
          :value="value"
          :rows="rows"
          :aria-label="ariaLabel || placeholder"
          class="input__textarea"
          :maxlength="maxLength"
          :class="inputClass"
          v-bind="inputAttrs"
          @click="clickHandler"
          @focus="focusHandler"
          @blur="blurHandler"
          @keypress="keypressHandler"
          @keyup="$emit('keyup', $event)"
          @input="$emit('input', $event.target.value)"
          @keydown="$emit('keydown', $event)"
        />
        <span v-if="isLabelVisible" class="input__label" :class="labelClass">
          {{ label }}
        </span>
        <span class="input__placeholder" :class="placeholderClass">
          {{ placeholder }}
        </span>
        <a-spinner
          v-if="inProgress"
          disable-transition
          :size="20"
          :stroke="5"
          class="input__icon"
        />
        <a-icon
          v-else-if="isIconVisible"
          v-bind="dynamicIconSettings"
          :is-button="isIconButton"
          :no-hover="!isIconHoverable"
          :original="isIconOriginal"
          class="input__icon"
          @click="onIconClick"
          @blur="onIconBlur"
        />
        <i
          v-else-if="isClearButtonVisible"
          class="close-icon close-icon_sm"
          @click="$emit('input', '')"
        />
      </div>
      <div v-else :class="preFilledWrapperClass">
        <a-icon
          :width="19"
          :height="19"
          v-bind="dynamicIconSettings"
          is-button
          class="input__prefilled-icon"
          @click="exitPrefilledMode"
        />
        <div class="input__prefilled-content">
          <div class="input__prefilled-label">{{ label || placeholder }}</div>
          <div class="input__prefilled-value">{{ value }}</div>
        </div>
      </div>
    </transition>
    <a-validation-message :error="inputError" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import mixins from '@/utils/mixins'
import {
  propValidator,
  generalValidation,
  PROP_TYPES
} from '@fmpedia/validators'
import AIcon, { ICON } from 'shared/AIcon'
import ASpinner from 'shared/ASpinner'
import AValidationMessage from 'shared/AValidationMessage'

export const INPUT_STYLE = {
  /** Bordered input (for now is the most used input in the project) **/
  STYLE_1: 'STYLE_1',
  /** Non-bordered input **/
  STYLE_2: 'STYLE_2',
  /** Non-bordered underlined input (for now is used only in Ebook widget) **/
  STYLE_3: 'STYLE_3',
  /** Only border-bottom is visible (used on commercial page) **/
  STYLE_4: 'STYLE_4'
}

const INPUT_STYLE_VALUES = Object.values(INPUT_STYLE)

export const INPUT_CLASS = 'input__input'

export const INPUT_TYPE = {
  PASSWORD: 'password',
  TEXT: 'text',
  EMAIL: 'email',
  URL: 'url',
  TEL: 'tel',
  NUMBER: 'number'
}

export const PRE_FILLED_BG_COLOR = {
  YELLOW: 'yellow'
}

const PRE_FILLED_CLASS = {
  [PRE_FILLED_BG_COLOR.YELLOW]: 'input__prefilled-wrapper_yellow'
}

export default {
  name: 'ATextInput',
  mixins: [mixins.validation],
  components: { AIcon, ASpinner, AValidationMessage },
  props: {
    value: propValidator([PROP_TYPES.STRING], false, ''),
    label: propValidator([PROP_TYPES.STRING], false),
    placeholder: propValidator([PROP_TYPES.STRING], false),
    multiline: propValidator([PROP_TYPES.BOOLEAN], false, false),
    disableGeneralValidation: propValidator([PROP_TYPES.BOOLEAN], false, false),
    rows: propValidator([PROP_TYPES.STRING, PROP_TYPES.NUMBER], false),
    required: propValidator([PROP_TYPES.BOOLEAN], false, false),
    prefilledMode: propValidator([PROP_TYPES.BOOLEAN], false, false),
    prefilledColor: propValidator([PROP_TYPES.STRING], false, null, prop =>
      Object.values(PRE_FILLED_BG_COLOR).includes(prop)
    ),
    error: propValidator([PROP_TYPES.OBJECT], false, () => ({})),
    type: propValidator([PROP_TYPES.STRING], false, INPUT_TYPE.TEXT, value =>
      Object.values(INPUT_TYPE).includes(value)
    ),
    passwordIcon: propValidator([PROP_TYPES.BOOLEAN], false, false),
    searchIcon: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideLabel: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideLabelWhenPlaceholderIsVisible: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      false
    ),
    maxLength: propValidator(
      [PROP_TYPES.STRING, PROP_TYPES.NUMBER],
      false,
      9999
    ),
    touchOnBlur: propValidator([PROP_TYPES.BOOLEAN], false, false),
    inputStyle: propValidator(
      [PROP_TYPES.STRING],
      false,
      INPUT_STYLE.STYLE_1,
      value => INPUT_STYLE_VALUES.includes(value)
    ),
    disabled: propValidator([PROP_TYPES.BOOLEAN], false, false),
    inProgress: propValidator([PROP_TYPES.BOOLEAN], false, false),
    blinkBorder: propValidator([PROP_TYPES.BOOLEAN], false, false),
    focusOnMount: propValidator([PROP_TYPES.BOOLEAN], false, false),
    clearable: propValidator([PROP_TYPES.BOOLEAN], false, false),
    ariaLabel: propValidator([PROP_TYPES.STRING], false),
    inputId: propValidator([PROP_TYPES.STRING], false)
  },
  data() {
    return {
      ICON,
      INPUT_TYPE,
      prefilledModeVal: this.value,
      isFocused: false,
      isPasswordShown: false
    }
  },
  validations: {
    value: {
      generalValidation
    }
  },
  computed: {
    ...mapGetters({
      isAppleBrowser: 'isAppleBrowser'
    }),
    inputAttrs() {
      return {
        ...(this.inputId ? { id: this.inputId } : {})
      }
    },
    isLabelVisible() {
      if (
        this.hideLabel ||
        !this.label ||
        (this.hideLabelWhenPlaceholderIsVisible &&
          !this.isFocused &&
          !this.value)
      ) {
        return false
      }

      return true
    },
    isIconVisible() {
      const isPasswordWithIcon =
        this.type === INPUT_TYPE.PASSWORD && this.passwordIcon
      const isSearchIcon = this.searchIcon && (!this.clearable || !this.value)
      return (
        this.$_validation_hasValidationError(this.inputError) ||
        isPasswordWithIcon ||
        isSearchIcon
      )
    },
    isClearButtonVisible() {
      return this.clearable && !!this.value
    },
    isPasswordInput() {
      return this.type === INPUT_TYPE.PASSWORD && this.passwordIcon
    },
    isSearchInput() {
      return !this.isPasswordInput && !!this.searchIcon
    },
    isIconHoverable() {
      if (this.currentInputIcon === ICON.ALERT_CIRCLE) return false

      return !this.clearable
    },
    isIconButton() {
      return [ICON.PENCIL, ICON.EYE, ICON.MAGNIFIER].includes(
        this.currentInputIcon
      )
    },
    currentInputIcon() {
      return this.dynamicIconSettings.icon
    },
    isIconOriginal() {
      return this.dynamicIconSettings.icon !== ICON.MAGNIFIER
    },
    dynamicIconSettings() {
      if (this.$_validation_hasValidationError(this.inputError)) {
        return {
          icon: ICON.ALERT_CIRCLE,
          ariaLabel: 'Alert',
          width: 16,
          height: 16
        }
      }

      if (this.isPrefilled) {
        return {
          icon: ICON.PENCIL,
          ariaLabel: 'Edit',
          width: 23,
          height: 14
        }
      }

      if (this.isPasswordInput) {
        return {
          icon: ICON.EYE,
          ariaLabel: 'Show/Hide password',
          width: 23,
          height: 14
        }
      }

      if (this.isSearchInput) {
        return {
          icon: ICON.MAGNIFIER,
          ariaLabel: 'Search',
          width: 23,
          height: 14
        }
      }

      return {}
    },

    isInternalValidationPassed() {
      return this.disableGeneralValidation || !this.$v.value.$error
    },
    inputError() {
      return this.disableGeneralValidation
        ? this.error
        : this.error && this.error.$error
        ? this.error
        : this.$v.value
    },
    isPrefilled() {
      return this.prefilledMode && this.prefilledModeVal
    },
    inputWrapperClass() {
      return {
        input_focused: this.isFocused,
        'input_placeholder-as-label':
          (this.value && !this.label) || this.isFocused,
        input_disabled: this.disabled,
        input_multiline: this.multiline,
        'input_show-icon': this.isIconVisible || this.isClearButtonVisible,
        input_error: this.$_validation_hasValidationError(this.inputError),
        input_prefilled: this.isPrefilled,
        'input_style-2': this.inputStyle === INPUT_STYLE.STYLE_2,
        'input_apple-browser': this.isAppleBrowser,
        'input_style-3': this.inputStyle === INPUT_STYLE.STYLE_3,
        'input_style-4': this.inputStyle === INPUT_STYLE.STYLE_4,
        'blinking-border': this.blinkBorder
      }
    },
    inputClass() {
      return [
        INPUT_CLASS,
        { 'input_text-area': this.multiline },
        { error: this.$_validation_hasValidationError(this.inputError) },
        { focused: this.isFocused || this.value?.length || this.hideLabel }
      ]
    },
    labelClass() {
      return {
        required: this.required
      }
    },
    placeholderClass() {
      return {
        error: this.$_validation_hasValidationError(this.inputError),
        focused: !this.label && (this.isFocused || this.value?.length),
        /** Temporary commented, can be removed after full check **/
        // focused_safari:
        //   (this.isFocused || this.value.length) &&
        //   this.isAppleBrowser &&
        //   !this.label,
        hidden:
          (this.isFocused || this.value?.length) &&
          (this.hideLabel || this.label),
        required:
          this.required &&
          ((this.value && !this.label) || this.isFocused || !this.label)
      }
    },
    inputType() {
      /**
       * maxlength attribute is ignored for type number.
       * That's why we use type text with additional attribute pattern="\d"
       * ToDo: pattern doesn't work properly. Will be implement when required.
       */
      if (this.type === INPUT_TYPE.NUMBER) return INPUT_TYPE.TEXT

      if (this.type !== INPUT_TYPE.PASSWORD) return this.type

      return !this.isPasswordShown ? INPUT_TYPE.PASSWORD : INPUT_TYPE.TEXT
    },
    preFilledWrapperClass() {
      return ['input__prefilled-wrapper', PRE_FILLED_CLASS[this.prefilledColor]]
    }
  },
  methods: {
    setPrefilledModeValue(newVal) {
      this.prefilledModeVal = newVal
    },
    exitPrefilledMode() {
      this.prefilledModeVal = null
    },
    clickHandler(e) {
      this.$emit('click', e)
    },
    focusHandler(e) {
      this.exitPrefilledMode()
      this.isFocused = true
      this.$emit('focus', e)
    },
    blurHandler(event) {
      this.isFocused = false
      if (this.touchOnBlur) {
        this.$v.$touch()
        if (this.error.$touch) {
          this.error.$touch()
        }
      }

      this.$emit('blur', event)
    },
    touchInnerValidation() {
      this.$v.$touch()
    },
    keypressHandler() {
      this.$emit('keypress', this.value)
    },
    onIconBlur(event) {
      this.$emit('blur:icon', event)
    },
    onIconClick() {
      const iconHandler = {
        [ICON.EYE]: this.onShowPassword,
        [ICON.MAGNIFIER]: this.onSearch
      }

      const handler = iconHandler[this.dynamicIconSettings.icon]

      if (this.$helper.checkIfFunction(handler)) {
        handler()
      }
    },
    onShowPassword() {
      this.isPasswordShown = !this.isPasswordShown
    },
    onSearch() {
      this.$emit('search', this.value)
    },
    focusInput() {
      if (this.$refs.input) {
        this.$refs.input.focus()
      }
    }
  },
  mounted() {
    if (this.focusOnMount) {
      setTimeout(this.focusInput)
    }
  }
}
</script>

<style lang="scss" scoped>
.input__wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 40px;
  background-color: $c--white;
  border: 2px solid $c--black;
  transition: border-color 150ms;

  .input__component-wrapper {
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 100%;
    background-color: inherit;
  }

  .input__input {
    width: 100%;
    max-height: 100%;
    padding: 11px 15px;

    font-size: 16px;
    line-height: 22px;
    background: transparent;
    color: black;

    &::placeholder {
      color: #4a4a4a;
    }
  }

  .input__textarea {
    height: 100%;
  }

  $transform: transform 150ms ease-out, font-size 150ms ease-out, opacity 150ms,
    color 150ms ease-out;

  .input__placeholder,
  .input__label {
    position: absolute;
    left: 11px;
    top: 9px;
    padding: 0 4px;

    font-size: 16px;
    line-height: 22px;
    color: #4a4a4a;
    background-color: inherit;
    transition: $transform;
    pointer-events: none;

    &.hidden {
      opacity: 0;
    }
  }

  .input__placeholder {
    max-height: 22px;
    overflow: hidden;
  }

  .input__label {
    color: $c--black;
    font-size: 11px;
    line-height: 14px;
    transform: translateY(-130%);
  }

  .input__icon,
  .close-icon {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
  }

  .close-icon {
    right: 15px;
  }

  &.input_placeholder-as-label .input__placeholder {
    transform: translateY(-130%);
    font-size: 11px;
    line-height: 14px;
  }

  &.input_show-icon .input__input {
    width: calc(100% - 23px);
  }

  &.input_focused .input__label,
  &.input_focused .input__placeholder {
    color: $c--main;
  }

  .input__input.input_text-area {
    resize: none;
  }

  &.input_focused {
    border-color: $c--main;
  }

  &.input_error {
    border-color: $c--error;
  }

  &.input_error .input__label {
    color: $c--error;
  }

  &.input_multiline {
    height: inherit;
    min-height: inherit;
    max-height: inherit;
  }

  &.input_disabled {
    pointer-events: none;
    background-color: $c--disabled;
    border-color: $c--disabled-border;

    .input__component-wrapper {
      background-color: $c--disabled;
    }

    .input__placeholder,
    .input__label,
    .input__input {
      color: $c--disabled-text;
    }

    .input__placeholder {
      background-color: $c--disabled;
    }
  }

  &.input_error.input_placeholder-as-label {
    .input__placeholder {
      color: $c--error;
    }
  }
}

.input__wrapper.input_prefilled {
  border: none;

  .input__prefilled-wrapper {
    display: flex;
    align-items: center;
    height: 40px;

    &_yellow {
      background-color: $c--yellow;
    }
  }

  .input__prefilled-icon {
    margin-right: 12px;
    cursor: pointer;
  }

  .input__prefilled-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .input__prefilled-label {
    font-size: 11px;
    line-height: 15px;
    color: #4a4a4a;
  }

  .input__prefilled-value {
    font-size: 16px;
  }
}

.input__wrapper.input_style-4 {
  border: 1px solid $c--gray-border;
  background-color: $c--gray-dark;
  border-radius: 4px;

  $height: (
    $above-desktop-xl: 44px
  );

  @include setStyleByBreakpoint('height', $height);

  .input__component-wrapper {
    border-radius: 4px;
  }

  &.input_focused {
    border-color: $c--white;

    .input__placeholder {
      display: none;
    }
  }

  &.input_disabled {
    border-color: transparent;

    .input__placeholder,
    .input__input,
    .input__component-wrapper {
      background-color: inherit;
      color: $c--gray;
    }

    .input__placeholder {
      left: 0;
    }

    .input__input {
      padding-left: 0;
    }
  }

  &.input_error {
    border-color: $c--error-border;
  }

  .input__placeholder {
    color: $c--gray;
    left: 14px;
    padding: 0;
    max-height: unset;
    top: 10px;

    @include textBodySm;
  }

  .input__input {
    padding: 10px 14px;
    font-size: 18px;
    line-height: 28px;
    color: $c--white;

    $font-size: (
      $above-desktop-xl: 18px,
      $mobile: 16px
    );

    $line-height: (
      $above-desktop-xl: 28px,
      $mobile: 24px
    );

    @include setStyleByBreakpoint('line-height', $line-height);
    @include setStyleByBreakpoint('font-size', $font-size);
  }

  .input__input.focused {
    outline: none;
  }

  .input__label {
    left: 0;
    padding: 0;
    transform: translateY(-215%);

    @include desktop-sm {
      transform: translateY(-170%);
    }
  }
}

.input__wrapper.input_style-3 {
  border: none;
  background: transparent;
  padding: 0 0 5px 0;
  border-bottom: 2px solid rgba(#fff, 0.5);

  .input__input.focused {
    font-size: 14px;
    font-weight: 600;
    color: $c--white;
    padding: 11px 0;
  }

  .input__label,
  span.input__placeholder {
    left: 0;

    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background-color: transparent;
  }

  &.input_error.input_placeholder-as-label {
    .input__placeholder {
      color: $c--white;
    }
  }

  &.input_error .input-group__error {
    color: $c--black;
  }
}

.input__wrapper.input_style-2 {
  border: none;
}
</style>
